/**
 * This set of hooks is how we'll interact with the groupUserStore. The idea is to provide a simple api to get what
 * we need from the store without having to use `dispatch`, `connect`, `mapStoreToProps`, and all that stuff
 * in the components.
 */

import { useSelector, useDispatch } from 'react-redux'
import apiUtils from '../../global/utils/api';
import { parseQueryArgs, selectSingleById } from '../../global/utils/storeUtils';

// import all of the actions from the store
import {
  fetchGroupUserList
  , fetchGroupUserListAtEndpoint
  , fetchListIfNeeded
  , fetchSingleGroupUser
  , fetchSingleGroupUserAtEndpoint
  , sendCreateGroupUser
  , sendUpdateGroupUser
  , sendUpdateManyGroupUsers
  , sendBulkUploadGroupUsers
  , sendDeleteGroupUser
  , invalidateQuery
  // , invalidateQueries
  , addGroupUserToList
  , fetchSingleIfNeeded
  , resendGroupInvite,
  myGroupUsersEndpoint,
  groupUsersForMyGroupEndpoint
} from './groupUserStore';
import {
  useGetResourceById
  , useGetResource
  , useGetResourceList
  , useMutateResource
} from '../../global/utils/serviceHooks';


// Define the hooks that we'll use to manage data in the components

// CREATE

/**
 * Use this hook to handle the creation of a new groupUser.
 * @param {Object} initialState - The initial state of the groupUser (optional)
 * @param {Function} handleResponse - The function to call when the groupUser is successfully created
 * 
 * @returns an object containing fetch info and the following:
 * - `newGroupUser` as `data`: the new groupUser object as it currently exists in state, initially the default groupUser
 * - `handleChange`: standard form change handler to be used in the form
 * - `handleSubmit`: standard form submit handler to be used in the form
 * - `setFormState`: a way to handle form state changes in the component instead of `handleChange`, rarely needed but sometimes necessary
 * @example // to use in a component
 * // access the create action and fetch the default groupUser
 * const { data: newGroupUser, handleChange, handleSubmit, ...groupUserQuery } = useCreateGroupUser({
 *   // optional, anything we want to add to the default object
 *   initialState: {
 *     someKey: 'someValue'
 *   }
 *   // optional, callback function that receives the response from the server
 *   , handleResponse: (groupUser, error) => {
 *     if(error || !groupUser) {
 *       alert(error || "An error occurred.")
 *     }
 *     history.push(`/group-users/${groupUser._id}`)
 *   }
 * });
 * 
 * return (
 *   <WaitOn query={groupUserQuery}>
 *     <GroupUserForm
 *       groupUser={groupUser}
 *       handleSubmit={handleSubmit}
 *       handleChange={handleChange}
 *     />
 *   </WaitOn>
 * )
 */
export const useCreateGroupUser = ({ initialState = {}, onResponse = () => { } }) => {
  const dispatch = useDispatch();
  // set up groupUser specific stuff to be used by the shared hook
  const defaultGroupUserQuery = useGetDefaultGroupUser();
  const sendMutation = (mutatedGroupUser) => dispatch(sendCreateGroupUser(mutatedGroupUser));

  // the hook will return everything the caller needs to create a new groupUser
  return useMutateResource({ resourceQuery: defaultGroupUserQuery, sendMutation, initialState, onResponse, isCreate: true });
}

// READ

/**
 * NOTE: If you are using this because you want to create a new groupUser, try `useCreateGroupUser`
 * instead. It handles everything for you!
 * 
 * @param {boolean} forceFetch - optional override to force a fetch from the server
 * @returns an object containing fetch info and eventually the defaultGroupUser (as `data`)
 * @returns a refetch function for convenience (will probably never be used for default groupUser, but keeps things consistent)
 */
export const useGetDefaultGroupUser = (forceFetch = false) => {
  // leverage existing hooks to get the default groupUser (using 'default' as the id will return the default groupUser from the server)
  return useGetGroupUserById('default', forceFetch);
}


/**
 * This hook will check for a fresh groupUser in the store and fetch a new one if necessary
 * 
 * @param {string} id - the id of the groupUser to be fetched
 * @param {boolean} forceFetch - optional override to force a fetch from the server
 * @returns an object containing fetch info and eventually the groupUser (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetGroupUserById = (id, forceFetch = false) => {
  const dispatch = useDispatch();
  // set up groupUser specific stuff to be used by the shared hook
  const groupUserStore = useSelector(({ groupUser }) => groupUser);
  const fetchGroupUser = forceFetch ? fetchSingleGroupUser : fetchSingleIfNeeded;
  const sendFetchById = (id) => dispatch(fetchGroupUser(id));
  const sendInvalidateSingle = (id) => dispatch(invalidateQuery(id));

  // return the (now groupUser specific) hook
  return useGetResourceById({ id, fromStore: groupUserStore, sendFetchById, sendInvalidateSingle });

}

/**
 * This hook will check for a fresh groupUser in the store and fetch a new one if necessary
 * 
 * @param {...string | object | null} args - accepts two optional arguments: a string (endpoint) or an object (listArgs) or both as (endpoint, listArgs)
 * @returns an object containing fetch info and eventually the groupUser (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetGroupUser = (...args) => {
  const dispatch = useDispatch();
  const { endpoint, listArgs } = parseQueryArgs(args);
  // set up groupUser specific stuff to be used by the shared hook
  const groupUserStore = useSelector(({ groupUser }) => groupUser);
  // const fetchGroupUser = endpoint ? fetchSingleGroupUserAtEndpoint : fetchSingleGroupUser;
  const fetchGroupUser = fetchSingleGroupUserAtEndpoint;
  const sendFetchSingle = (queryString) => dispatch(fetchSingleIfNeeded(queryString, fetchGroupUser));
  const sendInvalidateSingle = (queryString) => dispatch(invalidateQuery(queryString));
  // return the (now groupUser specific) hook
  return useGetResource({ listArgs, fromStore: groupUserStore, sendFetchSingle, sendInvalidateSingle, endpoint });
}

/**
 * This hook will check for a fresh list in the store and fetch a new one if necessary
 * 
 * @param {...string | object | null} args - accepts two optional arguments: a string (endpoint) or an object (listArgs) or both as (endpoint, listArgs)
 * @returns an object containing fetch info and eventually the group list (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetGroupUserList = (...args) => {
  const dispatch = useDispatch();
  const { endpoint, listArgs } = parseQueryArgs(args);
  // set up group specific stuff to be used by the shared hook
  const groupUserStore = useSelector(({ groupUser }) => groupUser);
  // if an endpoint was passed in, use that, otherwise use the default
  const fetchGroupUsers = endpoint ? fetchGroupUserListAtEndpoint : fetchGroupUserList;
  const fetchGroupUsersIfNeeded = (queryString) => fetchListIfNeeded(queryString, fetchGroupUsers);
  const sendFetchList = (queryString) => dispatch(fetchGroupUsersIfNeeded(queryString));
  const sendInvalidateList = (queryString) => dispatch(invalidateQuery(queryString));

  // return the (now group specific) hook
  return useGetResourceList({ listArgs, fromStore: groupUserStore, sendFetchList, sendInvalidateList, endpoint });
}
export const useLoggedInGroupUser = (groupId, skipFetch = false) => {
  // fetch the logged in list and then filter down by groupId and return the matching groupUser or null
  // skip the fetch by passing in null instead of the endpoint
  const { data: groupUsers, isSuccess, isError } = useGetGroupUserList(skipFetch ? null : myGroupUsersEndpoint);
  if(!groupId) return null;
  if(isError) return null;
  if(!isSuccess) return 'loading';
  const matchingGroupUser = groupUsers?.find(groupUser => groupUser._group === groupId) || null;
  return matchingGroupUser;
}


// UPDATE

/**
 * Use this hook to access the `sendUpdateGroupUser` action
 * 
 * Useful if you want to update a groupUser that you already have access to
 * 
 * NOTE: Check out `useGetUpdatableGroupUser` if you want to fetch and update a groupUser
 * 
 * @returns the sendUpdateGroupUser action wrapped in dispatch
 * @example // to use in a component
 * // access the update action
 * const { sendUpdateGroupUser } = useUpdateGroupUser();
 * // dispatch the update action
 * sendUpdateGroupUser(updatedGroupUser);
 */
export const useUpdateGroupUser = () => {
  const dispatch = useDispatch();
  return {
    // return the update action
    sendUpdateGroupUser: (updatedGroupUser) => dispatch(sendUpdateGroupUser(updatedGroupUser))
  }
}

export const useUpdateManyGroupUsers = ({ endpoint, groupId, userId }) => {
  const dispatch = useDispatch();
  return (ids, updates) => dispatch(sendUpdateManyGroupUsers({ endpoint, ids, groupId, userId, updates }))
}

export const useBulkUploadGroupUsers = ({ groupId }) => {
  const dispatch = useDispatch();
  return (file) => dispatch(sendBulkUploadGroupUsers({ groupId, file }))
}

/**
 * Use this hook to handle the update of an existing groupUser.
 * @param {string} id - the id of the groupUser to be updated.
 * @param {Object} options - an object that expects an optional onResponse function that receives the updated groupUser and error.
 * 
 * @returns an object containing fetch info and the following:
 * - `groupUser` as `data`: the groupUser object as it currently exists in state
 * - `handleChange`: standard form change handler to be used in the form
 * - `handleSubmit`: standard form submit handler to be used in the form
 * - `setFormState`: a way to handle form state changes in the component instead of `handleChange`, rarely needed but sometimes necessary
 * @example // to use in a component
 * // fetch the groupUser and access everything needed to handle updating it
 * const { data: groupUser, handleChange, handleSubmit, ...groupUserQuery } = useGetUpdatableGroupUser(groupUserId, {
 *   // optional, callback function to run after the request is complete
 *   onResponse: (updatedGroupUser, error) => {
 *     if(error || !updatedGroupUser) {
 *       alert(error || "An error occurred.")
 *     }
 *     history.push(`/group-users/${groupUserId}`)
 *   }
 * });
 * 
 * return (
 *   <WaitOn query={groupUserQuery}>
 *     <GroupUserForm
 *       groupUser={groupUser}
 *       handleSubmit={handleSubmit}
 *       handleChange={handleChange}
 *     />
 *   </WaitOn>
 * )
 */
export const useGetUpdatableGroupUser = (id, { groupId, endpoint, onResponse = () => { } } = {}) => {
  const dispatch = useDispatch();
  // set up groupUser specific stuff to be used by the shared hook
  // use the existing hook to get the groupUserQuery
  const groupUserQuery = useGetGroupUser(groupUsersForMyGroupEndpoint(groupId, id));
  const sendMutation = (mutatedGroupUser) => dispatch(sendUpdateGroupUser({ endpoint, method: 'PUT', _id: id, ...mutatedGroupUser }));
  // return the (now groupUser specific) hook
  return useMutateResource({ resourceQuery: groupUserQuery, sendMutation, onResponse, id });

}


// DELETE

/**
 * Use this hook to access the `sendDeleteGroupUser` action
 * 
 * @returns the sendDeleteGroupUser action wrapped in dispatch
 * 
 * @example // to use in a component
 * // access the delete action
 * const { sendDeleteGroupUser } = useDeleteGroupUser();
 * // dispatch the delete action
 * sendDeleteGroupUser(groupUserId);
 */
export const useDeleteGroupUser = () => {
  const dispatch = useDispatch();
  return {
    // return the delete action
    sendDeleteGroupUser: (id) => dispatch(sendDeleteGroupUser(id))
  }
}

// OTHERS

/**
 * @returns the `addGroupUserToList` action wrapped in dispatch
 */
export const useAddGroupUserToList = () => {
  const dispatch = useDispatch();
  return {
    addGroupUserToList: (groupUserId, listArgs) => dispatch(addGroupUserToList({ id: groupUserId, queryKey: apiUtils.queryStringFromObject(listArgs) || "all" }))
  }
}

/**
 * NOTE: Only use this if you're sure the groupUser is already in the store. WILL NOT fetch from the server.
 * @param {string} id - the id of the groupUser that you want to grab from the store
 * @returns the groupUser from the store's byId map
 */
export const useGroupUserFromMap = (id) => {
  const groupUser = useSelector(({ groupUser: groupUserStore }) => selectSingleById(groupUserStore, id));
  return groupUser
}

export const useResendGroupInvite = () => {
  const dispatch = useDispatch();
  return ({ _group, _id }) => dispatch(resendGroupInvite({ _group, _id }));
}

/**
 * NOTE: if you have the groupUser id, it's better to use `useGroupUserFromMap` to get the groupUser from the store
 * but if you don't have the id, you can use this hook to query the store for the groupUser you want
 * @param {Function} filter - a function that takes a groupUser and returns true if it's the one you want
 * @returns the result of the filter function passed to the Array.find() method where the array is the list of all groupUsers in the store
 */
export const useFindGroupUserInStore = (filter) => {
  const groupUserMap = useSelector(({ groupUser }) => groupUser.byId);
  const groupUserList = Object.values(groupUserMap);
  return groupUserList.find(filter);
}

export const useGetMyGroupUsers = (query = {}) => {
  return useGetGroupUserList(myGroupUsersEndpoint, query);
}

export const useGetGroupUserForMyGroup = ({ groupId, groupUserId }) => {
  const endpoint = groupUsersForMyGroupEndpoint(groupId, groupUserId);
  return useGetGroupUser(endpoint);
}
